import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { Button, Input } from "antd";
import { useDebounce } from "../utils/input";
import { useItemsFine } from "../actions/app/items";
import log from "cslog";
import { getImageFileURL, getPublicImageURL } from "../_helpers/image";
import styles from "./styles/MyCollections.module.css";
import classNames from "classnames";
import Icon from "@ant-design/icons";
import CreationsSelection from "./CreationsSelection";
import LoadingBox from "../common/LoadingBox";

/**
 * CreationsPicker is a React component that allows users to search and select multiple creations
 * from a list of available items. The component uses a modal to display the list of creations,
 * which can be filtered by a search term. Selected creations are managed in a local state and
 * can be added or removed by the user. The component makes use of debouncing for the search
 * input to optimize performance and fetches data using a custom hook.
 */
function CreationsPicker({
    value,
    onChange,
    creations_filter_query = "filter[user_created][_eq]=$CURRENT_USER",
    allow_template = true,
    allow_creations = true,
    allow_multiple = true,
}) {
    const [open_templates, setOpenTemplates] = useState(false);
    const [open_creations, setOpenCreations] = useState(false);

    const [search_term, setSearchTerm] = useState("");
    const search_query = useDebounce(search_term, 1000);
    const {
        isSuccess: successTemplates,
        isLoading: loadingTemplates,
        data: dataTemplates,
    } = useItemsFine(
        "qdb_creation_template",
        `fields=id,title,preview&filter[tags][qdb_tag_id][name][_contains]=${search_query}&sort=-date_created`,
        !!search_query && open_templates,
    );
    const {
        isSuccess: successCreations,
        isLoading: loadingCreations,
        data: dataCreations,
    } = useItemsFine(
        "qp_creation",
        `fields=id,title,preview&sort=-date_created&filter[type][_eq]=singlepage&${creations_filter_query}`,
        open_creations,
    );
    const [options, setOptions] = useState([]);

    useEffect(() => {
        if (successTemplates) {
            setOptions(dataTemplates);
        }
    }, [open_templates, successTemplates, dataTemplates]);

    useEffect(() => {
        if (successCreations) {
            setOptions(dataCreations);
        }
    }, [open_creations, successCreations, dataCreations]);

    const [selected_creations, setSelectedCreations] = useState([]);

    log.d(dataTemplates, "Creation picker data");

    log.d(dataCreations, "Creation picker dataCreations");

    log.d(selected_creations, "Selected Creations");

    useEffect(() => {
        setSelectedCreations([]);
        setSearchTerm("");
    }, [value]);

    const selectCreation = (creation_id) => {
        if (selected_creations.includes(creation_id)) {
            setSelectedCreations(
                selected_creations.filter((item) => item !== creation_id),
            );
        } else if (allow_multiple) {
            setSelectedCreations([...selected_creations, creation_id]);
        } else {
            setSelectedCreations([creation_id]);
        }
    };

    return (
        <>
            <div className={styles.button_group}>
                {allow_template && !(!allow_multiple && value?.length > 0) && (
                    <Button
                        type="primary"
                        ghost
                        onClick={() => {
                            setOpenCreations(false);
                            setOptions([]);
                            setOpenTemplates(true);
                        }}
                    >
                        Add Templates
                    </Button>
                )}
                {allow_creations && !(!allow_multiple && value?.length > 0) && (
                    <Button
                        type="primary"
                        ghost
                        onClick={() => {
                            setOpenTemplates(false);
                            setOptions([]);
                            setOpenCreations(true);
                        }}
                    >
                        Add Creations
                    </Button>
                )}
            </div>
            <CreationsSelection value={value} onChange={onChange} />
            <Modal
                title="Select Creations"
                open={open_templates || open_creations}
                width={"60vw"}
                onOk={() => {
                    setOpenTemplates(false);
                    setOpenCreations(false);
                }}
                onCancel={() => {
                    setOpenTemplates(false);
                    setOpenCreations(false);
                }}
                footer={[
                    <Button
                        type="primary"
                        disabled={selected_creations.length === 0}
                        onClick={() => {
                            // onChange(selected_creations);
                            // onChange(selected_creations.map(item => ({
                            //     collection: "qdb_creation_template",
                            //     id: item
                            // })));
                            onChange([
                                ...(value?.length > 0 ? value : []),
                                ...(selected_creations?.length > 0
                                    ? selected_creations.map((item) => ({
                                          collection: open_templates
                                              ? "qdb_creation_template"
                                              : "qp_creation",
                                          id: item,
                                      }))
                                    : []),
                            ]);
                            setOpenTemplates(false);
                            setOpenCreations(false);
                            setOptions([]);
                        }}
                    >
                        Add {selected_creations.length} Creations
                    </Button>,
                    <Button
                        type="default"
                        onClick={() => setSelectedCreations([])}
                    >
                        Reset
                    </Button>,
                    <Button
                        type="default"
                        onClick={() => {
                            setOpenTemplates(false);
                            setOpenCreations(false);
                            setOptions([]);
                        }}
                    >
                        Cancel
                    </Button>,
                ]}
            >
                <Input
                    value={search_term}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search"
                    suffix={<Icon type="user" />}
                />
                {options?.length > 0 && (
                    <div className={styles.creation_picker_wrapper}>
                        {options.map((item) => (
                            <CreationCard
                                item={item}
                                selected={selected_creations.includes(item.id)}
                                setSelected={selectCreation}
                            />
                        ))}
                    </div>
                )}
                {(loadingCreations || loadingTemplates) && <LoadingBox />}
            </Modal>
        </>
    );
}

export default CreationsPicker;

function CreationCard({ item, selected, setSelected }) {
    return (
        <div
            className={classNames(styles.creation_item, {
                [styles.selected]: selected,
            })}
            onClick={() => setSelected(item.id)}
        >
            <img src={getPublicImageURL(item.preview)} alt="preview" />
            <div className={styles.creation_details}>
                <h4 style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                    {item.title}
                </h4>
            </div>
        </div>
    );
}
